/**
 * ✎ list — Daily or ETF Performance's symbols, preset plus yours.
 *
 * A member hides and shows the preset's symbols and adds their own, which go
 * into a category of theirs ("My symbols" unless they type another, decision
 * 3); the admin's categories stay the admin's. The author edits the preset
 * itself: add with a category, remove, and the label beside a symbol.
 */
import { useEffect, useState, type CSSProperties } from "react";
import type { BriefBoardConfig, BriefListItem } from "@qufolio/model";
import {
  USER_CATEGORY, addSymbol, memberAdded, memberCategories, removeSymbol, resolvedList, unhideSymbol,
  type EditContext, type ListName,
} from "../../lib/brief-board";
import { Chip } from "./cells";

/**
 * The author's label edit, in place. `addSymbol` would re-add the item at the
 * end of the list, and a label is not a reason for a row to move.
 */
function withLabel(config: BriefBoardConfig, list: ListName, symbol: string, label: string): BriefBoardConfig {
  const cur = config.preset.lists?.[list] ?? {};
  const items = (cur.items ?? []).map((i) => {
    if (i.symbol !== symbol) return i;
    const { label: _old, ...rest } = i;
    return label.trim() ? { ...rest, label: label.trim() } : rest;
  });
  return { ...config, preset: { ...config.preset, lists: { ...config.preset.lists, [list]: { ...cur, items } } } };
}

export function ListEditor({
  title, list, config, ctx, onChange, onClose,
}: {
  title: string;
  list: ListName;
  config: BriefBoardConfig;
  ctx: EditContext;
  onChange: (next: BriefBoardConfig) => void;
  onClose: () => void;
}) {
  const member = ctx.member;
  const preset = config.preset.lists?.[list];
  const mine = config.mine?.lists?.[list];
  const hidden = new Set(mine?.hidden ?? []);
  const categories = resolvedList(preset, mine).categories;
  // A member files only under categories of their own; the author's stay the author's (decision 3).
  const choices = member ? memberCategories(preset, mine) : categories;
  const added = memberAdded(preset, mine);

  const [symbol, setSymbol] = useState("");
  const [label, setLabel] = useState("");
  const [category, setCategory] = useState(member ? USER_CATEGORY : (categories[0] ?? ""));
  const [typed, setTyped] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const add = () => {
    if (!symbol.trim()) return;
    const cat = typed.trim() || category;
    const item: BriefListItem = { symbol };
    if (cat) item.category = cat;
    if (!member && label.trim()) item.label = label.trim();
    onChange(addSymbol(config, ctx, list, item));
    setSymbol("");
    setLabel("");
    setTyped("");
  };

  const changes = (mine?.added?.length ?? 0) + (mine?.hidden?.length ?? 0);

  return (
    <div style={BACKDROP} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-label={`${title} — list`} style={PANEL}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, gap: 12 }}>
          <b>{title} — list</b>
          <span style={MUTED}>
            {member ? `preset · ${changes} change${changes === 1 ? "" : "s"} of yours` : "editing the preset"}
          </span>
        </div>
        <div style={LIST}>
          {(preset?.items ?? []).map((i) => {
            const off = hidden.has(i.symbol);
            return (
              <div key={`p:${i.symbol}`} style={ROW}>
                <span style={SYM}>{i.symbol}</span>
                {member ? (
                  <span style={NAME}>{i.label ?? ""}</span>
                ) : (
                  <input
                    aria-label={`Label for ${i.symbol}`}
                    defaultValue={i.label ?? ""}
                    placeholder="label"
                    onBlur={(e) => e.target.value !== (i.label ?? "") && onChange(withLabel(config, list, i.symbol, e.target.value))}
                    onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
                    style={{ ...INPUT, flex: 1 }}
                  />
                )}
                {i.category && <span style={TAG}>{i.category}</span>}
                {member && <span style={off ? { ...TAG, textDecoration: "line-through" } : TAG}>{off ? "preset · hidden" : "preset"}</span>}
                {member ? (
                  off ? (
                    <Chip onClick={() => onChange(unhideSymbol(config, ctx, list, i.symbol))}>show</Chip>
                  ) : (
                    <Chip onClick={() => onChange(removeSymbol(config, ctx, list, i.symbol))}>hide</Chip>
                  )
                ) : (
                  <Chip onClick={() => onChange(removeSymbol(config, ctx, list, i.symbol))}>remove</Chip>
                )}
              </div>
            );
          })}
          {member &&
            added.map((i) => (
              <div key={`m:${i.symbol}`} style={ROW}>
                <span style={SYM}>{i.symbol}</span>
                <span style={NAME}>{i.label ?? ""}</span>
                <span style={TAG}>{i.category || USER_CATEGORY}</span>
                <span style={{ ...TAG, color: "var(--qf-accent)", borderColor: "var(--qf-accent)" }}>yours</span>
                <Chip onClick={() => onChange(removeSymbol(config, ctx, list, i.symbol))}>remove</Chip>
              </div>
            ))}
        </div>
        <div style={{ ...ROW, flexWrap: "wrap", borderBottom: "none", paddingTop: 8 }}>
          <input
            aria-label="Add symbol"
            placeholder="+ symbol"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && add()}
            style={{ ...INPUT, width: 80 }}
          />
          {!member && (
            <input aria-label="Label" placeholder="label" value={label} onChange={(e) => setLabel(e.target.value)}
              style={{ ...INPUT, width: 110 }} />
          )}
          <span style={MUTED}>category:</span>
          {choices.map((c) => (
            <Chip key={c} on={!typed.trim() && category === c} onClick={() => { setCategory(c); setTyped(""); }}>{c}</Chip>
          ))}
          {!member && <Chip on={!typed.trim() && category === ""} onClick={() => { setCategory(""); setTyped(""); }}>none</Chip>}
          <input
            aria-label="New category"
            placeholder="new category"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            style={{ ...INPUT, width: 120 }}
          />
          <Chip primary onClick={add} disabled={!symbol.trim()}>Add</Chip>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <Chip primary onClick={onClose}>Done</Chip>
        </div>
      </div>
    </div>
  );
}

const BACKDROP: CSSProperties = {
  position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
  background: "color-mix(in srgb, var(--qf-background) 55%, transparent)", padding: 16,
};
const PANEL: CSSProperties = {
  width: 560, maxWidth: "100%", maxHeight: "85vh", display: "flex", flexDirection: "column",
  border: "1px solid var(--qf-border)", borderRadius: 8, background: "var(--qf-surface)", color: "var(--qf-text)",
  padding: "10px 12px", fontSize: 13, boxShadow: "0 8px 24px color-mix(in srgb, var(--qf-background) 70%, transparent)",
};
const LIST: CSSProperties = { overflow: "auto", minHeight: 0, flex: 1 };
const ROW: CSSProperties = {
  display: "flex", gap: 8, alignItems: "center", padding: "3px 0",
  borderBottom: "1px solid color-mix(in srgb, var(--qf-border) 55%, transparent)",
};
const SYM: CSSProperties = { width: 60, fontWeight: 600, flex: "none" };
const NAME: CSSProperties = { flex: 1, color: "var(--qf-text-muted)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" };
const TAG: CSSProperties = {
  font: "11px ui-monospace, monospace", border: "1px solid var(--qf-border)", borderRadius: 4, padding: "0 5px",
  color: "var(--qf-text-muted)", whiteSpace: "nowrap",
};
const MUTED: CSSProperties = { color: "var(--qf-text-muted)", fontSize: 13 };
const INPUT: CSSProperties = {
  background: "var(--qf-background)", color: "var(--qf-text)", border: "1px solid var(--qf-border)",
  borderRadius: 4, padding: "2px 4px", fontSize: 13,
};
